
import { useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { 
  Product, 
  deleteProduct,
  addProduct,
  getCurrentUserOrganizationId
} from '@/lib/supabase/products';

interface UseProductOperationsProps {
  organizationId: string | null;
  products: Product[];
  filteredProducts: Product[];
  setEditingProductId: (id: string | null) => void;
  setAddingProduct: (isAdding: boolean) => void;
  fetchProducts: () => Promise<void>;
  shopId?: string;  // Paramètre shopId
}

export const useProductOperations = ({
  organizationId,
  products,
  filteredProducts,
  setEditingProductId,
  setAddingProduct,
  fetchProducts,
  shopId
}: UseProductOperationsProps) => {
  const { toast } = useToast();

  // Handle adding a new product
  const handleAddProduct = useCallback(() => {
    console.log("handleAddProduct called in useProductOperations with shop ID:", shopId);
    // Set adding product to true to show form
    setAddingProduct(true);
    // We don't navigate here - that's handled at the parent level if needed
  }, [setAddingProduct, shopId]);

  // Handle deleting a product
  const handleDeleteProduct = async (id: string) => {
    try {
      console.log('Attempting to delete product with ID:', id);
      
      const success = await deleteProduct(id);
      
      if (success) {
        console.log('Product successfully deleted');
        toast({
          title: 'Produit supprimé',
          description: 'Le produit a été supprimé avec succès',
        });
        await fetchProducts(); // Refresh products list after deletion
      } else {
        throw new Error('Failed to delete product');
      }
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Erreur',
        description: `Impossible de supprimer le produit: ${error.message || 'Erreur inconnue'}`,
        variant: 'destructive',
      });
    }
  };

  // Handle bulk deleting products
  const handleBulkDeleteProducts = async (productIds: string[]) => {
    if (productIds.length === 0) return;

    try {
      console.log('Attempting to delete products:', productIds);
      
      const deletePromises = productIds.map(id => deleteProduct(id));
      
      await Promise.all(deletePromises);
      
      toast({
        title: 'Succès',
        description: `${productIds.length} produit${productIds.length > 1 ? 's' : ''} supprimé${productIds.length > 1 ? 's' : ''} avec succès`,
      });
      
      await fetchProducts(); // Refresh products list after bulk deletion
    } catch (error: any) {
      console.error('Error deleting products:', error);
      toast({
        title: 'Erreur',
        description: `Impossible de supprimer les produits sélectionnés: ${error.message || 'Erreur inconnue'}`,
        variant: 'destructive',
      });
    }
  };

  return {
    handleAddProduct,
    handleDeleteProduct,
    handleBulkDeleteProducts,
    shopId
  };
};
